import { useEffect, useState, type MouseEvent } from 'react'
import { cn } from '../lib/cn'

export interface SecaoDoSumario {
  /** O `id` do elemento na página. É para ele que o atalho rola. */
  id: string
  rotulo: string
}

/**
 * "Nesta página": os atalhos para as partes de uma tela longa, presos sob a barra superior.
 *
 * Existe porque algumas telas são, por natureza, um caderno inteiro — o fechamento do mês junta
 * indicadores, desconto, leitura automática, a conta e as fontes, e passava de cinco mil pixels de
 * rolagem sem nenhum ponto de apoio. Partir em abas esconderia o que o fiscal precisa ver junto
 * para assinar; o sumário mantém tudo na página e dá o caminho.
 *
 * O atalho rola em vez de trocar o endereço: um `#indicadores` na URL viraria uma entrada a mais no
 * histórico a cada clique, e o "voltar" do navegador passaria a andar dentro da tela em vez de sair
 * dela. O foco vai junto para a seção — sem isso, quem usa teclado vê a tela rolar e continua com o
 * foco no atalho, lá em cima.
 *
 * @param props as seções, na ordem em que aparecem
 * @returns a barra de atalhos
 */
export function SumarioDaPagina({ secoes, rotulo = 'Nesta página' }: { secoes: SecaoDoSumario[]; rotulo?: string }) {
  const atual = useSecaoVisivel(secoes)

  function irPara(evento: MouseEvent<HTMLAnchorElement>, id: string) {
    const alvo = document.getElementById(id)
    if (!alvo) {
      return
    }
    evento.preventDefault()
    const semMovimento = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    alvo.scrollIntoView({ behavior: semMovimento ? 'auto' : 'smooth', block: 'start' })
    if (!alvo.hasAttribute('tabindex')) {
      alvo.setAttribute('tabindex', '-1')
    }
    alvo.focus({ preventScroll: true })
  }

  return (
    <nav
      aria-label={rotulo}
      className="sticky top-[3.75rem] z-10 -mx-1 bg-fundo/95 px-1 py-2 backdrop-blur supports-[backdrop-filter]:bg-fundo/80"
    >
      <ul className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 shadow-cartao">
        {secoes.map((secao) => {
          const ativa = atual === secao.id
          return (
            <li key={secao.id} className="shrink-0">
              <a
                href={`#${secao.id}`}
                onClick={(evento) => irPara(evento, secao.id)}
                aria-current={ativa ? 'location' : undefined}
                className={cn(
                  'block whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                  ativa ? 'bg-niteroi-700 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                )}
              >
                {secao.rotulo}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

/**
 * Qual seção está sendo lida agora: a que cruza a faixa logo abaixo do sumário.
 *
 * Sem `IntersectionObserver` (o jsdom dos testes não tem), nenhuma fica acesa — o sumário continua
 * funcionando como lista de atalhos, só não acompanha a rolagem.
 */
function useSecaoVisivel(secoes: SecaoDoSumario[]): string | null {
  const [atual, definirAtual] = useState<string | null>(null)
  const chave = secoes.map((secao) => secao.id).join('|')

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return
    }
    const observador = new IntersectionObserver(
      (entradas) => {
        const visivel = entradas.find((entrada) => entrada.isIntersecting)
        if (visivel) {
          definirAtual(visivel.target.id)
        }
      },
      // A faixa de leitura: do fim da barra superior até um terço da tela.
      { rootMargin: '-120px 0px -66% 0px' },
    )
    for (const id of chave.split('|')) {
      const elemento = document.getElementById(id)
      if (elemento) {
        observador.observe(elemento)
      }
    }
    return () => observador.disconnect()
  }, [chave])

  return atual
}
